import { z } from 'zod'
import { parseKwacha } from '@/lib/money'

export function amountToNgwee(text: string): number | null {
  const n = parseKwacha(text)
  return n !== null && n > 0 ? n : null
}
/** Zero is allowed here (a cash limit of nothing means "hold no cash"). */
export function amountOrZeroToNgwee(text: string): number | null {
  const n = parseKwacha(text)
  return n !== null && n >= 0 ? n : null
}

export const policyFormSchema = z
  .object({
    mode: z.enum(['record_only', 'budgets_only', 'budgets_and_expenses_over_threshold', 'budgets_and_all_expenses']),
    threshold: z.string(),
    note: z.string().trim().max(300, 'Use a shorter note'),
  })
  .superRefine((v, ctx) => {
    if (v.mode === 'budgets_and_expenses_over_threshold' && amountToNgwee(v.threshold) === null) {
      ctx.addIssue({ code: 'custom', path: ['threshold'], message: 'Enter the amount from which an expense needs approval, like 1000' })
    }
  })

export const departmentFormSchema = z.object({
  code: z.string().trim().regex(/^[A-Za-z][A-Za-z0-9]{1,15}$/, 'Use 2 to 16 letters or numbers, like YOUTH'),
  name: z.string().trim().min(2, 'Give the department a name').max(80, 'Use a shorter name'),
})

export const nameSchema = z.object({ name: z.string().trim().min(2, 'Type at least 2 letters').max(80, 'Use a shorter name') })

export const settingsFormSchema = z.object({
  churchName: z.string().trim().min(2, 'Give the church a name').max(80, 'Use a shorter name'),
  cashLimit: z.string().refine((v) => amountOrZeroToNgwee(v) !== null, 'Enter an amount like 1000, or 0'),
  slaDays: z.string().regex(/^\d{1,2}$/, 'A whole number of days from 1 to 60').refine((v) => Number(v) >= 1 && Number(v) <= 60, 'A whole number of days from 1 to 60'),
  matchDays: z.string().regex(/^\d{1,2}$/, 'A whole number of days from 0 to 30').refine((v) => Number(v) >= 0 && Number(v) <= 30, 'A whole number of days from 0 to 30'),
})

export const personFormSchema = z
  .object({
    fullName: z.string().trim().min(2, 'Type the full name').max(80, 'Use a shorter name'),
    email: z.string().trim().email('Enter a valid email address'),
    phone: z.string().trim().refine((v) => v === '' || /^\+260\d{9}$/.test(v), 'Write the phone like +260971234567, or leave it empty'),
    role: z.enum(['admin', 'auditor', 'dept_treasurer', 'dept_viewer']),
    departmentId: z.string(),
  })
  .superRefine((v, ctx) => {
    if ((v.role === 'dept_treasurer' || v.role === 'dept_viewer') && v.departmentId === '') {
      ctx.addIssue({ code: 'custom', path: ['departmentId'], message: 'Pick the department' })
    }
  })
